import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApiClient } from '@app/lib/api/api-client';
import {
  ModerationAreas,
  ModeratorRole,
  PermissionsRoleResponse,
  UserInfoResponse,
  UserManagementResponse
} from '@app/lib/api/user/api.user.model';
import { RelationshipCountsRes } from '@app/lib/api/verification-requests/api.verification-request.models';
import { UserManagementReport } from '@app/shared/models/users-management';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  userInfo = new BehaviorSubject<any>({});

  private api: ApiClient;
  constructor(private http: HttpClient) {
    this.api = new ApiClient(this.http, { responseTimeout: environment.API_TIMEOUT });
  }

  getUserById(id: string): Observable<UserInfoResponse> {
    return this.api.user.getUserById(id);
  }

  getUserInfo(userId: string): Observable<any> {
    return this.api.user.getUserInfo(userId);
  }

  getTotalsUser(roles: any, statuses: any, keyword: string) {
    return this.api.user.getTotalsUser(roles, statuses, keyword);
  }

  getById(id: string): Observable<any> {
    return this.http.get<any>('assets/mock/user-by-id.json');
  }

  updateUser(userId: string, userDetail: Partial<any>): Observable<any> {
    return this.api.user.updateUser(userId, userDetail);
  }

  delete(id: string) {
    return this.api.user.delete(id);
  }

  getUserManagementReport(): Observable<UserManagementReport> {
    return this.api.user.getUserManagementReport();
  }

  getUserManagement(
    pageNum: number,
    pageSize: number,
    status = ''
  ): Observable<UserManagementResponse> {
    return this.api.user.getUserManagement(pageNum, pageSize, status);
  }

  getModeratorRole(): Observable<ModeratorRole[]> {
    return this.api.user.getModeratorRole();
  }

  getModerationAreas(textSearch: string, isOtaOnly = false): Observable<ModerationAreas[]> {
    return this.api.user.getModerationAreas(textSearch, isOtaOnly);
  }

  getTokenToLoadIframe(): Observable<string> {
    return this.api.user.getTokenToLoadIframe();
  }

  getPermissionCodeList() {
    return this.api.user.getPermissionCodeList();
  }

  getRelationshipCounts(usernameOrId: string): Observable<RelationshipCountsRes> {
    return this.api.user.getRelationshipCounts(usernameOrId);
  }

  getPermissionsTree(nodeId?: string): Observable<PermissionsRoleResponse> {
    return this.api.user.getPermissionsTree(nodeId);
  }

  getCensorManagementList(
    pageNum: number,
    pageSize: number,
    nodeId: string,
    countryCode: string,
    textSearch?: string,
    module?: string,
    status?: string,
    role?: string
  ) {
    return this.api.user.getCensorManagementList(
      pageNum,
      pageSize,
      nodeId,
      countryCode,
      textSearch,
      module,
      status,
      role
    );
  }

  getDetailCensorManagement(
    nodeId: string,
    countryCode: string,
    module?: string,
    status?: string,
    role?: string
  ) {
    return this.api.user.getDetailCensorManagement(nodeId, countryCode, module, status, role);
  }

  getCensorVerificationPerformanceDetail(
    userId: string,
    objectType: string,
    rootId: string,
    fromDate?: string,
    toDate?: string
  ) {
    return this.api.user.getCensorVerificationPerformanceDetail(
      userId,
      objectType,
      rootId,
      fromDate,
      toDate
    );
  }

  getCensorPerformanceDetail(
    userId: string,
    objectType: string,
    rootId: string,
    fromDate?: string,
    toDate?: string
  ) {
    return this.api.user.getCensorPerformanceDetail(userId, objectType, rootId, fromDate, toDate);
  }

  getCensorPerformance(userId: string, rootId: string, fromDate?: string, toDate?: string) {
    return this.api.user.getCensorPerformance(userId, rootId, fromDate, toDate);
  }
}
